// src/page/DoNotSell.jsx
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

export default function DoNotSell() {
  const [optCookieSet, setOptCookieSet] = useState(false);
  const gpc = useMemo(() => {
    try {
      // Browser-level signal (Global Privacy Control)
      // https://globalprivacycontrol.org/
      return Boolean(navigator?.globalPrivacyControl);
    } catch {
      return false;
    }
  }, []);

  const setOptOutCookie = () => {
    // 2 years opt-out cookie; adjust domain/path if needed
    const twoYears = 60 * 60 * 24 * 365 * 2;
    document.cookie = `do_not_sell=1; Max-Age=${twoYears}; Path=/; SameSite=Lax`;
    setOptCookieSet(true);
  };

  const sections = [
    { id: "overview", label: "Overview" },
    { id: "what-is-sale", label: "What “Sale” / “Share” Means" },
    { id: "what-we-sell", label: "Categories Potentially Sold/Shared" },
    { id: "how-to-opt", label: "How to Opt Out" },
    { id: "gpc", label: "Global Privacy Control (GPC)" },
    { id: "ads", label: "Ads / Cookies Choices" },
    { id: "verification", label: "Verification & Timing" },
    { id: "non-discrimination", label: "Non-Discrimination" },
    { id: "children", label: "Children" },
    { id: "contact", label: "Contact" },
    { id: "updated", label: "Last Updated" },
  ];

  return (
    <main className="max-w-6xl mx-auto px-4 py-12">
      {/* Breadcrumb */}
      <nav className="text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-[#0A2540]">Home</Link>
        <span className="mx-2">›</span>
        <span className="text-slate-600">Legal</span>
        <span className="mx-2">›</span>
        <span className="text-[#0A2540] font-medium">
          CA Residents – Do Not Sell or Share My Personal Information
        </span>
      </nav>

      {/* Title */}
      <header className="mb-8">
        <p className="text-center text-xs font-semibold uppercase tracking-wide text-[#0A2540]/70">
          California Consumer Privacy (CCPA/CPRA)
        </p>
        <h1 className="mt-2 text-center text-3xl md:text-4xl font-extrabold text-[#0A2540]">
          Do Not Sell or Share My Personal Information
        </h1>
        <p className="mt-3 text-center text-slate-600">
          For California residents only. This page explains your right to opt out
          of the “sale” or “sharing” of personal information and how to exercise it.
        </p>
      </header>

      <div className="grid lg:grid-cols-[260px,1fr] gap-10">
        {/* TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="px-4 py-3 border-b border-slate-200 font-semibold text-[#0A2540]">
                On this page
              </div>
              <ul className="p-3 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="block rounded px-3 py-2 text-slate-700 hover:bg-slate-50 hover:text-[#0A2540]"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Small GPC card */}
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-sm">
                <div className="font-semibold text-[#0A2540] mb-1">
                  Global Privacy Control
                </div>
                <p className="text-slate-600">
                  {gpc ? (
                    <span className="text-emerald-600 font-medium">
                      GPC detected — honored on this browser.
                    </span>
                  ) : (
                    <>
                      Your browser does not appear to send a GPC signal. You can enable
                      it in supported browsers/extensions.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </aside>

        {/* Article */}
        <article className="prose prose-slate max-w-none">
          {/* Overview */}
          <section id="overview" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Overview</h2>
            <p>
              Under the California Consumer Privacy Act (as amended by the CPRA),
              California residents have the right to direct a business that sells
              or shares their personal information to stop such sale/sharing. This
              page explains how MassTortPlaintiffs honors your choice.
            </p>
            <p>
              To learn more about our overall practices, please see our{" "}
              <Link to="/privacy" className="underline text-[#0A2540]">Privacy Policy</Link>{" "}
              and{" "}
              <Link to="/california-privacy" className="underline text-[#0A2540]">
                California Privacy Notice
              </Link>.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          {/* What is Sale/Share */}
          <section id="what-is-sale" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">What “Sale” / “Share” Means</h2>
            <p>
              “Sale” or “sharing” under California law may include disclosing personal
              information to third parties in exchange for money <em>or</em> for other
              valuable consideration (including certain advertising, analytics, or lead
              generation arrangements).
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          {/* Categories potentially sold/shared */}
          <section id="what-we-sell" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Categories Potentially Sold/Shared</h2>
            <p>Depending on your interactions with our site and services, the categories may include:</p>
            <ul>
              <li>Identifiers (e.g., IP address, contact information you submit)</li>
              <li>Internet or network activity (e.g., pages viewed, device data, session replay)</li>
              <li>General location data derived from IP address</li>
              <li>Inferences drawn from the above to improve services and advertising performance</li>
            </ul>
            <p className="mt-3">
              See our{" "}
              <Link to="/privacy" className="underline text-[#0A2540]">Privacy Policy</Link>{" "}
              for full details.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          {/* How to opt out */}
          <section id="how-to-opt" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">How to Opt Out</h2>
            <p className="mb-4">
              Choose any of the following methods. We will honor and record your preference:
            </p>

            {/* 1. Inline form (simple) */}
            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="px-4 py-3 border-b border-slate-200 font-semibold text-[#0A2540]">
                Submit an Opt-Out Request
              </div>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="p-4 grid md:grid-cols-2 gap-3"
              >
                <input
                  type="text"
                  placeholder="Full name"
                  className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[#C89B3C]"
                  required
                />
                <input
                  type="email"
                  placeholder="Email address"
                  className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[#C89B3C]"
                  required
                />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[#C89B3C]"
                />
                <input
                  type="text"
                  placeholder="State of residence (CA)"
                  className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[#C89B3C]"
                  defaultValue="California"
                />
                <textarea
                  placeholder="Additional details (optional)"
                  rows={3}
                  className="md:col-span-2 rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[#C89B3C]"
                />
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-5 py-2.5 font-semibold text-white hover:bg-[#B8872D] transition"
                  >
                    Send Opt-Out Request
                  </button>
                </div>
              </form>
            </div>

            {/* 2. Email & Mail */}
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-slate-200 p-4">
                <h3 className="m-0 text-lg font-semibold text-[#0A2540]">Email</h3>
                <p className="mt-2">
                  Send your request to{" "}
                  <a href="mailto:info@masstortplaintiffs.com" className="underline text-[#0A2540]">
                  info@masstortplaintiffs.com
                  </a>{" "}
                  with the subject <em>“Do Not Sell or Share My Personal Information”</em>.
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <h3 className="m-0 text-lg font-semibold text-[#0A2540]">Mail</h3>
                <p className="mt-2">
                  PO Box 214, Oceanside, NY 11572<br />
                  Attn: Privacy – Do Not Sell/Share
                </p>
              </div>
            </div>

            {/* 3. Opt-out cookie */}
            <div className="mt-6 rounded-lg border border-slate-200 p-4">
              <h3 className="m-0 text-lg font-semibold text-[#0A2540]">Set an Opt-Out Cookie</h3>
              <p className="mt-2">
                This browser-level preference helps us honor your choice for this device/browser.
                You may need to repeat this on other devices or after clearing cookies.
              </p>
              <button
                type="button"
                onClick={setOptOutCookie}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-4 py-2 font-semibold text-white hover:bg-[#0a2238] transition"
              >
                {optCookieSet ? "Opt-Out Cookie Set" : "Turn On Opt-Out Cookie"}
              </button>
            </div>

            <hr className="my-6 border-slate-200" />
          </section>

          {/* Global Privacy Control */}
          <section id="gpc" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Global Privacy Control (GPC)</h2>
            <p>
              We honor the browser’s <strong>Global Privacy Control</strong> signal where detected. When your
              browser sends a GPC signal, we treat it as a request to opt out of sale/share for that browser.
            </p>
            <p className="mt-2">
              Status on this browser:{" "}
              {gpc ? (
                <span className="text-emerald-600 font-medium">GPC detected</span>
              ) : (
                <span className="text-slate-600">not detected</span>
              )}
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          {/* Ads / Cookies */}
          <section id="ads" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Ads / Cookies Choices</h2>
            <p>
              You may also limit interest-based advertising via:
            </p>
            <ul>
              <li>Your browser’s cookie settings and ad-blocking tools</li>
              <li>Your mobile OS ad preferences (Limit Ad Tracking / Opt-out of Ads Personalization)</li>
              <li>
                Industry opt-outs such as{" "}
                <a className="underline text-[#0A2540]" href="https://optout.aboutads.info" target="_blank" rel="noreferrer">
                  aboutads.info/choices
                </a>{" "}
                and{" "}
                <a className="underline text-[#0A2540]" href="https://optout.networkadvertising.org" target="_blank" rel="noreferrer">
                  networkadvertising.org/choices
                </a>
              </li>
            </ul>
            <p className="mt-2">
              These tools are browser/device specific and may rely on cookies. You may need to repeat settings
              for each browser and device.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          {/* Verification */}
          <section id="verification" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Verification & Timing</h2>
            <p>
              For security, we may need to verify your identity (and an authorized agent’s authority, if applicable)
              before completing your request. We respond to verifiable requests within the timeframes required by law.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          {/* Non-discrimination */}
          <section id="non-discrimination" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Non-Discrimination</h2>
            <p>
              We will not discriminate against you for exercising any of your CCPA/CPRA rights, including the
              right to opt out of sale/share.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          {/* Children */}
          <section id="children" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Children</h2>
            <p>
              We do not knowingly sell or share the personal information of consumers under 16 years of age.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          {/* Contact */}
          <section id="contact" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Contact</h2>
            <p>
              Email:{" "}
              <a href="mailto:info@masstortplaintiffs.com" className="underline text-[#0A2540]">
              info@masstortplaintiffs.com
              </a>
              <br />
              Mail: PO Box 214, Oceanside, NY 11572
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          {/* Last Updated */}
          <section id="updated" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Last Updated</h2>
            <p>April 30, 2025</p>
          </section>
        </article>
      </div>
    </main>
  );
}
